import axios from '../../common/utils/interceptor';
import updateTaskItem from '../utils/updateTaskItem';
import { apiUrls, routeMappings } from '../../constants';
import { ASSESSMENT_EVALUATION_POLLING_TIME, RR_WITH_HURDLE_EVALUATION_START_TIME, TaskItemStatus } from '../constants';
import { checkIfRRAppliedButNotFullRR } from '../utils/checkifRRAppliedButNotFullRR';
import { markWorkflowEnd } from '../utils/markWorkflowEnd';
import { Task, TaskItem } from '../interfaces';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { currentWorkflowItemProvider } from '../../redux-store/providers/currentWorkflowItemProvider';

const getItemStatus = async (pendingItem: TaskItem): Promise<{ data?: TaskItem; error: string }> => {
  const response = { data: undefined, error: '' };

  try {
    const { data } = await axios.get(`${apiUrls.INITIALIZE_TASK}/${pendingItem?.id}`);
    response.data = data;
  } catch (e) {
    response.error = 'error';
    console.error('Error while fetching item status');
  }
  return response;
};

/**
 * Polling for Evaluation pending Task item
 */
export const usePollPendingAssessmentItem = (tasks: Task[]): boolean => {
  const history = useHistory();
  const { updateCurrentWorkflowItem } = currentWorkflowItemProvider();
  const [isLoading, setIsLoading] = useState(false);

  const rrAppliedButNotFullRR = checkIfRRAppliedButNotFullRR(tasks);
  const lastPendingTaskIndex = tasks.findIndex((task: Task) => task.status === TaskItemStatus.EVALUATION_PENDING);
  const pendingTask = tasks[lastPendingTaskIndex];
  const pendingItem = pendingTask && pendingTask.items.find((i) => i.status === TaskItemStatus.EVALUATION_PENDING);

  useEffect(() => {
    let itemStatusIntervalId: ReturnType<typeof setInterval>;
    let timeoutId: ReturnType<typeof setTimeout>;
    const pollPendingAssessmentItem = async (): Promise<void> => {
      if (pendingItem && !itemStatusIntervalId) {
        itemStatusIntervalId = setInterval(async () => {
          const { data } = await getItemStatus(pendingItem);
          if (data && data.status !== TaskItemStatus.EVALUATION_PENDING) {
            clearInterval(itemStatusIntervalId);
            if (data.status === TaskItemStatus.HURDLE_FAILED) {
              updateTaskItem({ ...pendingItem, taskId: pendingTask.id }, TaskItemStatus.HURDLE_FAILED);
              await markWorkflowEnd();
              history.push(routeMappings['TASKLIST_HURDLE_FAIL']);
            } else {
              updateTaskItem({ ...pendingItem, taskId: pendingTask.id }, TaskItemStatus.COMPLETED);
              setIsLoading(false);
            }
          }
        }, ASSESSMENT_EVALUATION_POLLING_TIME);
      }
    };

    if (pendingItem) {
      setIsLoading(true);
      if (rrAppliedButNotFullRR) {
        timeoutId = setTimeout(() => pollPendingAssessmentItem(), RR_WITH_HURDLE_EVALUATION_START_TIME);
      } else {
        pollPendingAssessmentItem();
      }
    }

    return (): void => {
      if (itemStatusIntervalId) {
        setIsLoading(false);
        clearInterval(itemStatusIntervalId);
      }
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [JSON.stringify(tasks), rrAppliedButNotFullRR, pendingItem, pendingTask, history, updateCurrentWorkflowItem]);

  return isLoading;
};
